import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Store the FCM token so we can deactivate it on logout
let storedFcmToken = null;

/**
 * Initialize push notifications on native platforms.
 * Call this after user login when on a native app.
 * @param {string} authToken - JWT auth token for API calls (axios header may not be set yet)
 */
export const initPushNotifications = async (authToken) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const permResult = await FirebaseMessaging.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push notification permission not granted');
      return;
    }

    // Get FCM registration token — on iOS, APNs registration must complete first.
    // Retry with delay to allow the APNs token to arrive.
    let token = null;
    const maxRetries = 5;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await FirebaseMessaging.getToken();
        token = result.token;
        break;
      } catch (tokenError) {
        console.warn(`getToken attempt ${attempt} failed:`, tokenError.message);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    if (!token) {
      console.error('Failed to get FCM token after all retries');
      return;
    }
    storedFcmToken = token;

    // Register token with backend
    try {
      await axios.post(
        `${API_URL}/api/notifications/register-device`,
        {
          token: token,
          platform: Capacitor.getPlatform()
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
    } catch (error) {
      console.error('Failed to register device token:', error);
    }

    // Listen for token refresh (e.g., after app reinstall or token rotation)
    FirebaseMessaging.addListener('tokenReceived', async (event) => {
      storedFcmToken = event.token;
      try {
        await axios.post(`${API_URL}/api/notifications/register-device`, {
          token: event.token,
          platform: Capacitor.getPlatform()
        });
      } catch (error) {
        console.error('Failed to register refreshed token:', error);
      }
    });

    // Handle foreground notifications
    FirebaseMessaging.addListener('notificationReceived', (notification) => {
      console.log('Push received in foreground:', notification);
    });

    // Handle notification tap (opens app from background/killed)
    FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
      const data = action.notification?.data;
      if (!data) return;

      // Navigate based on notification data
      if (data.ride_id) {
        window.location.href = `/ride/${data.ride_id}`;
      } else if (data.ride_request_id) {
        window.location.href = `/ride-request/${data.ride_request_id}`;
      } else if (data.day_booking_id) {
        window.location.href = `/day-booking/${data.day_booking_id}`;
      }
    });
  } catch (error) {
    console.error('Error initializing push notifications:', error);
  }
};

/**
 * Unregister device token on logout.
 */
export const unregisterPushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Deactivate token on backend before clearing listeners
    if (storedFcmToken) {
      try {
        await axios.delete(`${API_URL}/api/notifications/unregister-device`, {
          data: {
            token: storedFcmToken,
            platform: Capacitor.getPlatform()
          }
        });
      } catch (error) {
        console.error('Failed to unregister device token:', error);
      }
      storedFcmToken = null;
    }

    await FirebaseMessaging.removeAllListeners();
  } catch (error) {
    console.error('Error unregistering push notifications:', error);
  }
};
